// src/pages/Tarifs.js
// Page publique : Abonnements / Tarifs (multivendeur)
// Maxula

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiStar, FiZap, FiImage, FiTag } from 'react-icons/fi';
import { planAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const fmt = (v) => (typeof v === 'number' ? v.toLocaleString('fr-FR') : v);
const lim = (n, unit) => (n === -1 ? `${unit} illimité${unit.endsWith('s') ? '' : 's'}` : `${n} ${unit}`);

// Parse `features` quel que soit le format renvoyé par le backend
// (tableau, string JSON "[\"a\",\"b\"]", ou CSV "a,b").
// Le backend MySQL renvoie souvent ce champ comme une STRING -> .map() planterait.
const parseFeatures = (f) => {
  if (Array.isArray(f)) return f;
  if (typeof f === 'string' && f.trim()) {
    try {
      const parsed = JSON.parse(f);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return f.split(',').map((s) => s.trim()).filter(Boolean);
    }
  }
  return [];
};

const Tarifs = () => {
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth() || {};

  useEffect(() => {
    (async () => {
      try {
        const res = await planAPI.getPublic();
        setPlans(res.data?.data || []);
      } catch (e) {
        // silencieux
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const cta = (plan) => {
    if (user && (user.role === 'merchant' || user.role === 'admin')) {
      navigate('/merchant/subscription');
    } else if (user) {
      navigate('/merchant/subscription');
    } else {
      navigate('/register');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <div className="text-center mb-10">
        <h1 className="text-4xl font-extrabold text-gray-900">Nos abonnements vendeurs</h1>
        <p className="text-gray-500 mt-3 max-w-2xl mx-auto">
          Choisissez le pack adapté à votre activité et commencez à publier vos offres sur Maxula.
        </p>
      </div>

      <div className="flex items-center justify-center gap-3 mb-10">
        <span className={cycle === 'monthly' ? 'font-semibold' : 'text-gray-500'}>Mensuel</span>
        <button
          onClick={() => setCycle((c) => (c === 'monthly' ? 'yearly' : 'monthly'))}
          className="relative w-14 h-7 rounded-full bg-primary-600"
          aria-label="Cycle de facturation"
        >
          <span className={`absolute top-1 h-5 w-5 rounded-full bg-white transition-all ${cycle === 'yearly' ? 'left-8' : 'left-1'}`} />
        </button>
        <span className={cycle === 'yearly' ? 'font-semibold' : 'text-gray-500'}>
          Annuel <span className="text-green-600 text-xs">(2 mois offerts)</span>
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const price = plan.isFree ? 0 : cycle === 'yearly' ? plan.priceYearly : plan.priceMonthly;
            return (
              <div key={plan._id}
                className={`relative rounded-2xl border bg-white p-6 flex flex-col ${plan.isPopular ? 'border-primary-500 shadow-xl scale-[1.02]' : 'border-gray-200'}`}>
                {plan.isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-primary-600 text-white text-xs font-semibold flex items-center gap-1">
                    <FiStar size={12} /> Populaire
                  </div>
                )}
                <div className="text-xl font-bold" style={{ color: plan.color }}>{plan.name}</div>
                <p className="text-sm text-gray-500 mt-1 min-h-[40px]">{plan.description}</p>
                <div className="mt-4">
                  {plan.isFree ? (
                    <div className="text-3xl font-extrabold text-gray-900">Gratuit</div>
                  ) : (
                    <div className="flex items-end gap-1">
                      <span className="text-3xl font-extrabold text-gray-900">{fmt(price)}</span>
                      <span className="text-gray-500 mb-1">MAD/{cycle === 'yearly' ? 'an' : 'mois'}</span>
                    </div>
                  )}
                </div>
                <ul className="mt-5 space-y-2 text-sm flex-1">
                  <li className="flex items-center gap-2 text-gray-700"><FiTag className="text-green-600" /> {lim(plan.maxDeals, 'annonces')}</li>
                  <li className="flex items-center gap-2 text-gray-700"><FiImage className="text-green-600" /> {lim(plan.maxPhotos, 'photos')} / annonce</li>
                  <li className="flex items-center gap-2 text-gray-700"><FiCheck className="text-green-600" /> Commission {plan.commissionRate}%</li>
                  <li className="flex items-center gap-2 text-gray-700"><FiZap className="text-green-600" /> {lim(plan.featuredDeals, 'mises en avant')}</li>
                  {parseFeatures(plan.features).map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-600"><FiCheck className="text-gray-400" /> {f}</li>
                  ))}
                </ul>
                <button onClick={() => cta(plan)}
                  className="mt-6 w-full py-2.5 rounded-lg font-semibold bg-primary-600 text-white hover:bg-primary-700">
                  {plan.isFree ? 'Commencer gratuitement' : 'Choisir ce pack'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {!loading && plans.length === 0 && (
        <div className="text-center text-gray-500 py-12">Aucun pack disponible pour le moment.</div>
      )}
    </div>
  );
};

export default Tarifs;